// import components
import { DatePicker, showSnackbar, Tooltip } from 'components/shared';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import { StyledBaseQueueBox } from './BaseQueue.styles';

// import hooks
import { FC, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

// import utils
import { endOfToday, isAfter, isValid, startOfToday } from 'date-fns';
import {
  formatByLocale,
  fromISOString,
  getMinDate,
  toLocalISOString
} from 'utils/date';

// import types
import { BaseQueueProperties } from './BaseQueue.types';
import { Icon } from '@sdb/planning-ui';

const BaseQueue: FC<BaseQueueProperties> = ({
  queueName,
  columns,
  getBaseQueue,
  rowMapper
}) => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<Date | null>(startOfToday());
  const [endDate, setEndDate] = useState<Date | null>(endOfToday());
  const [pageSize, setPageSize] = useState<number>(10);

  const baseColumns = columns.map((key) => ({
    field: key,
    headerName: t(`adminPortal.${queueName}.${key}`),
    flex: 1
  }));

  const isDateInputValid = useMemo(() => {
    if (!(isValid(startDate) && isValid(endDate))) {
      return false;
    }

    if (startDate && endDate) {
      return !isAfter(startDate, endDate);
    }

    return false;
  }, [startDate, endDate]);

  const start = startDate ? toLocalISOString(startDate) : '';
  const end = endDate ? toLocalISOString(endDate) : '';

  const { data: baseQueue = [], isLoading } = useQuery(
    ['BaseQueue', start, end],
    async ({ signal }) => {
      const { data } = await getBaseQueue({
        params: {
          start,
          end
        },
        signal
      });
      return data;
    },
    {
      enabled: isDateInputValid && isAfter(startDate as Date, getMinDate())
    }
  );

  const onClickCopyToClipboardRow = (
    id: string | number,
    importedObject: string | undefined
  ) =>
    navigator.clipboard
      .writeText(importedObject || '')
      .then(() =>
        showSnackbar({
          severity: 'success',
          message: 'Item ' + t('copiedToClipboard', { name: id })
        })
      )
      .catch((error) => {
        console.error('Failed to copy text to clipboard:', error);
      });

  const rows = baseQueue
    .map((o) => (rowMapper ? rowMapper(o) : o))
    .map((o) => ({
      ...o,
      started: formatByLocale(fromISOString(o.started), 'Pp'),
      inserted: formatByLocale(fromISOString(o.inserted), 'Pp'),
      ended: formatByLocale(fromISOString(o.ended), 'Pp')
    }));

  return (
    <>
      <StyledBaseQueueBox>
        <DatePicker
          label={t('startDate')}
          onChange={(date) => {
            if (date) {
              setStartDate(date);
            }
          }}
          date={startDate}
        />
        <DatePicker
          label={t('endDate')}
          onChange={(date) => {
            if (date) {
              setEndDate(date);
            }
          }}
          date={endDate}
        />
        {!isDateInputValid && (
          <Tooltip
            title={
              t('dateRangeMismatch', {
                startDate: t('startDate'),
                endDate: t('endDate')
              }) || ''
            }
          >
            <Icon type="circle-exclamation" color="warning" />
          </Tooltip>
        )}
      </StyledBaseQueueBox>
      <TableCRUDLayout
        rows={rows}
        columns={baseColumns}
        loading={isLoading}
        initialState={{
          sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }]
          }
        }}
        pagination
        editable={false}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        onPaginationModelChange={({ pageSize }) => setPageSize(pageSize)}
        customActions={(id, row) => [
          {
            tooltipTitle: t('copyToClipboard'),
            icon: 'clipboard-list',
            onClick: () => onClickCopyToClipboardRow(id, row?.importedObject)
          }
        ]}
      />
    </>
  );
};

export default BaseQueue;
